// ═══════════════════════════════════════════════════════════════
// SEED: Catálogo de Roles v2 — pirámide operativa + roles laterales
// ═══════════════════════════════════════════════════════════════
//
// Ejecutar desde la consola del navegador, logueado como Wizard (desde el
// 29-sep-2026 las reglas dejan escribir `/roles` sólo al Wizard):
//   await __seedRolesV2()              — siembra los 11 roles del modelo v2
//   await __seedRolesV2({ dryRun:true }) — solo simula, no escribe
//
// IDEMPOTENTE: si ya existe un rol con el mismo `codigo`, lo actualiza con
// los permisos canónicos del seed (overwrite), salvo los pares que nacen
// apagados y el rol ya tenía: esos se conservan (`permisosAlResembrar`). Los
// campos `orden`, `nombre`, `color` SÍ se respetan si el admin los editó en la UI.
//
// Estrategia de permisos (decidida 2026-05-14): "amplio y después podo".
//   - Hoy el único usuario real del sistema es el Wizard. El resto de los
//     roles no-Wizard arrancan con TODO lo operativo (todos los módulos del
//     eje 'operativo', todas las acciones, scope 'todo') y se podan desde el
//     ABM de Roles cuando entren usuarios reales.
//   - Salvo las funciones con `naceApagada: true` en el catálogo (report
//     vxONAxDNRKyVcyAbIwTS, mesa del 19-oct-2026: «toda función nueva nace
//     apagada»): la siembra no las otorga a ningún rol, se tildan en
//     /permisos, y re-sembrar no le saca a un rol las que ya tenía tildadas.
//     Una función nueva de un módulo operativo sin la marca pone rojo
//     `src/core/lib/seedRolesV2.naceApagada.test.js`.
//   - Los módulos que la lista del 28-sep (y las decisiones del 24 y el 31-oct)
//     volvieron «sólo staff»
//     (`MODULOS_SOLO_STAFF`, `src/core/lib/_ejesCache.js`) los reciben enteros
//     los roles de staff y ningún otro (report LVyJbRGvZebhNU2MmO4G).
//   - ⚠️ `ejeDe` lee primero el eje persistido en `/modulos` (lo carga el
//     login): re-sembrar sólo poda lo que el eje de Firestore ya dice.
//   - El eje 'plataforma' (usuarios, roles, configuración) NO se amplía:
//     queda acotado por rol. Wizard saltea todos los checks por código.
//   - La lista operativa se DERIVA de MODULOS_CATALOGO (fuente de verdad) —
//     si se agrega un módulo operativo nuevo, los roles lo reciben al
//     re-seedear (evita el bug "módulo en el catálogo sin permiso en ningún
//     rol", que bloquearía requirePermiso en las Cloud Functions).
//   - El campo `extiende` SÍ se usa en runtime: el motor de permisos hereda
//     los del rol padre (`_resolverPermisosRol`, `functions/permisos.js`, y su
//     espejo en `src/core/lib/permisos.js`). Acá los permisos ya vienen
//     aplanados, así que heredar no suma nada; pero en un rol editado a mano
//     `extiende` le da los permisos del padre.
//
// Roles que se siembran:
//
//   Eje Operativo (cascada conceptual de menos a más amplio):
//     OPERADOR        → base. Empleado de empresa que ejecuta.
//     OWNER_EMPRESA   → todo sobre SU empresa + meta-permisos para gestionar usuarios.
//     CONSULTOR       → todo sobre sus N empresas asignadas.
//     SUPERVISOR      → todo sobre empresas de SUS consultores.
//     OPERACIONES     → todo sobre TODAS las empresas (staff Milbrands operativo).
//     WIZARD          → root. Saltea cualquier check. permisos[] vacío.
//
//   Roles laterales (no siguen cascada):
//     ASESOR              → mirada externa que sugiere y comenta.
//     CONTADOR            → acotado a área contable/fiscal.
//     AUDITOR             → solo lectura, con ventana temporal en asignación.
//     AGENCIA_PUBLICIDAD  → acotado a área marketing/ventas.
//     COLABORADOR_SOPORTE → suplencia temporal de consultor (vacaciones).
//
// NOTA: las descripciones de los roles laterales describen su INTENCIÓN de
// diseño (el objetivo del recorte). Hoy arrancan con operativo completo y se
// podan desde el ABM — ver "Estrategia de permisos" arriba.
//
// Después de este seed:
//   - Los roles legacy (suspendido, duenoCliente, etc.) SIGUEN existiendo.
//   - Se borran en B9 (cleanup) cuando ya nadie depende de ellos.
//   - B3 migra usuarios de vistaSlug → rolId nuevo.
// ═══════════════════════════════════════════════════════════════

import {
  collection, getDocs, doc, getDoc, setDoc, serverTimestamp, query, where,
} from 'firebase/firestore'
import { db } from '@core/lib/firebase'
import { COLLECTIONS } from '@core/lib/constants'
import { MODULOS_CATALOGO } from '@core/lib/modulos'
import { ejeDe, EJES } from '@core/lib/accionesPermiso'
import { MODULOS_SOLO_STAFF, MODULO_EJE_FALLBACK, getEjeCache } from '@core/lib/_ejesCache'

// ─────────────────────────────────────────────────────────────────
// 1. Helpers para construir listas de permisos sin repetir
// ─────────────────────────────────────────────────────────────────

const P = (modulo, accion, scope = 'todo', filtros = null) => {
  const p = { modulo, accion, scope }
  if (filtros) p.filtros = filtros
  return p
}

// Helper para "expandir" un permiso a múltiples acciones con mismo scope+filtros.
const M = (modulo, acciones, scope = 'todo', filtros = null) =>
  acciones.map(a => P(modulo, a, scope, filtros))

// ─────────────────────────────────────────────────────────────────
// 2. Permisos por rol
// ─────────────────────────────────────────────────────────────────
// Ver "Estrategia de permisos" en el encabezado. Todos los roles no-Wizard
// comparten PERMISOS_OPERATIVOS_COMPLETOS; sólo difieren en el eje plataforma.

// `usuarios-empresa` (gestionar colaboradores y asignaciones DE una empresa)
// es operativo por eje pero NO es de todos: es el gate server de los 4
// callables de colaboradores (usuariosAdmin.js, T7a 8-sep-2026). Decisión de
// Mariano: "los permisos son siempre para abajo" — lo tiene el Owner sobre su
// empresa y el staff de equipo (CONSULTOR / SUPERVISOR / OPERACIONES) sobre
// las de su equipo; un OPERADOR/ASESOR/CONTADOR con `asignar-rol` podía
// auto-promoverse a Owner (hallazgo de `security`, compuerta 2 de T7a).
const MODULOS_OPERATIVOS_ACOTADOS = ['usuarios-empresa']

// Las funciones que la siembra otorga: todas menos las que nacen apagadas.
const sembrables = (m) => m.funciones.filter(f => !f.naceApagada)

// TODO lo operativo (salvo los acotados y lo que nace apagado), scope 'todo'.
// Derivado del catálogo (no se hardcodea). Base compartida por todos los roles
// no-Wizard.
const PERMISOS_OPERATIVOS_COMPLETOS = MODULOS_CATALOGO
  .filter(m => ejeDe(m.codigo) === EJES.OPERATIVO && !MODULOS_OPERATIVOS_ACOTADOS.includes(m.codigo))
  .flatMap(m => sembrables(m).map(f => P(m.codigo, f.codigo, 'todo')))

const GESTION_COLABORADORES = MODULOS_CATALOGO
  .filter(m => m.codigo === 'usuarios-empresa')
  .flatMap(m => sembrables(m).map(f => P(m.codigo, f.codigo, 'todo')))

// Lo que la lista del 28-sep pasó a «sólo staff» (`MODULOS_SOLO_STAFF`,
// `_ejesCache.js`): el cliente deja de recibirlo y el staff lo conserva entero,
// como lo tenía por la base operativa (report LVyJbRGvZebhNU2MmO4G).
const PERMISOS_SOLO_STAFF = MODULOS_CATALOGO
  .filter(m => MODULOS_SOLO_STAFF.includes(m.codigo))
  .flatMap(m => sembrables(m).map(f => P(m.codigo, f.codigo, 'todo')))

// Un par una sola vez por rol. Mientras el eje persistido en `/modulos` (que en
// una sesión logueada manda sobre el código) diga `operativo`, esos pares
// llegan también por la base operativa.
const sinRepetir = (permisos) => {
  const vistos = new Set()
  return permisos.filter(p => {
    const k = `${p.modulo}.${p.accion}`
    if (vistos.has(k)) return false
    vistos.add(k)
    return true
  })
}

const PARES_NACE_APAGADA = new Set(MODULOS_CATALOGO.flatMap(m =>
  m.funciones.filter(f => f.naceApagada).map(f => `${m.codigo}.${f.codigo}`)))

/**
 * Los permisos con que queda un rol que ya existía al re-sembrarlo: los
 * canónicos del seed más los pares que nacen apagados y el rol ya tenía
 * (tildados en /permisos o dados por un `seed-permiso-*.cjs`). Nacer apagado
 * es que la siembra no lo otorga, no que lo revoca.
 * @param {Array<{modulo: string, accion: string}>} canonicos
 * @param {Array<{modulo: string, accion: string}>|undefined} existentes
 */
export function permisosAlResembrar(canonicos, existentes) {
  const conservados = (Array.isArray(existentes) ? existentes : [])
    .filter(p => PARES_NACE_APAGADA.has(`${p?.modulo}.${p?.accion}`))
  return [...canonicos, ...conservados]
}

// ─── La poda, a la vista antes de dispararla (report LVyJbRGvZebhNU2MmO4G) ─
// Re-sembrar PODA: lo que un rol tenía y el seed ya no le da, lo pierde. Estas
// tres lecturas son lo que `seedRolesV2({ dryRun: true })` imprime para que la
// poda se revise antes de correrla en real.
const clave = (p) => `${p?.modulo}.${p?.accion}`

/** Los pares que un rol existente pierde al re-sembrarlo. */
export function paresQueSePodan(canonicos, existentes) {
  const quedan = new Set(permisosAlResembrar(canonicos, existentes).map(clave))
  return (Array.isArray(existentes) ? existentes : []).map(clave).filter(k => !quedan.has(k))
}

/**
 * Los módulos decididos cuyo eje en `/modulos` no es el del código. En una
 * sesión logueada ese eje manda (`ejeDe` lee primero el cache), así que la
 * siembra NO poda esos módulos hasta que se cambie en Items del sidebar.
 */
export function ejesDistintosDelCodigo(ejeEnModulos = getEjeCache) {
  return Object.entries(MODULO_EJE_FALLBACK)
    .filter(([c, eje]) => ejeEnModulos(c) !== undefined && ejeEnModulos(c) !== eje)
    .map(([modulo, decidido]) => ({ modulo, enModulos: ejeEnModulos(modulo), decidido }))
}

/**
 * Los roles que el seed NO toca (creados a mano) y no son de staff, con los
 * pares «sólo staff» que conservan: ésos se podan a mano en /permisos.
 */
export function rolesAjenosConSoloStaff(rolesDocs, codigosDelSeed) {
  return (rolesDocs || [])
    .filter(r => !codigosDelSeed.includes(r.codigo) && !(r.aplicaA || []).includes('consultor'))
    .map(r => ({ codigo: r.codigo, pares: (r.permisos || []).filter(p => MODULOS_SOLO_STAFF.includes(p?.modulo)).map(clave) }))
    .filter(r => r.pares.length > 0)
}

// ─── Eje plataforma — acotado por rol (NO se amplía) ──────────────
// Sólo el Wizard tiene plataforma completa (vía shortcut en código). Estos
// son los recortes que ya tenían los roles de staff / owner antes.
const PLATAFORMA_OWNER_EMPRESA = [
  ...M('configuracion', ['ver', 'editar'], 'todo'),
  ...GESTION_COLABORADORES,
]
const PLATAFORMA_CONSULTOR = [
  ...PLATAFORMA_OWNER_EMPRESA,
  ...PERMISOS_SOLO_STAFF,
  ...M('usuarios', ['ver'], 'todo'),
  ...M('roles',    ['ver'], 'todo'),
]
const PLATAFORMA_SUPERVISOR = [
  ...PLATAFORMA_CONSULTOR,
  ...M('usuarios', ['editar', 'cambiarRol'], 'todo'),
]
const PLATAFORMA_OPERACIONES = [
  ...PLATAFORMA_SUPERVISOR,
  // `asignar` (19-oct-2026, decisión de Mariano — report AcvSZKp8mCZxdytgvqs5):
  // sin él, OPERACIONES creaba y editaba usuarios desde Gestión › Usuarios pero
  // el alcance rebotaba a mitad del guardado (`asignarEmpresaUsuario` exige
  // `usuarios.asignar`, functions/usuariosAdmin.js). Va acá y no tildado a
  // mano: la re-siembra sólo conserva los pares que nacen apagados.
  ...M('usuarios', ['crear', 'invitar', 'desactivar', 'asignar'], 'todo'),
  // `planes` (15-ago-2026, decisión de Mariano — ítem 22 `#15`). Antes ningún
  // rol salvo WIZARD lo tenía, y eso producía una ESCRITURA PARCIAL: la UI de
  // /planes deja entrar a OPERACIONES —`usePermisos().puedo` cortocircuita
  // para admin, `puedeHacerServer` NO tiene ese atajo—, el ABM por Firestore
  // pasaba por `isConsultor()` y el callable de los campos de gasto rebotaba.
  // Quedaba un plan creado sin `syncConfig`, o sea `plan_off` silencioso para
  // toda empresa que lo tomara.
  ...M('planes', ['ver', 'editar'], 'todo'),
  // Observabilidad y eventos (27-oct-2026, decisión de Mariano — report
  // LVyJbRGvZebhNU2MmO4G): la prueba en seco de la poda mostró que la
  // re-siembra le sacaba a OPERACIONES estos pares, que tenía tildados a mano
  // (los dos módulos son `plataforma`, que no llega por la base operativa).
  // `instalaciones.ver` es la misma clase: Mariano decidió el 21-sep que lo ve
  // OPERACIONES y lo daba sólo `seed-permiso-instalaciones-ver.cjs`, así que la
  // re-siembra también se lo podaba. De observabilidad, sólo esos dos:
  // `correr-drill-alertas` emite alertas y mails reales y no lo tenía.
  ...M('observabilidad', ['ver', 'probar-endpoints-ml'], 'todo'),
  // `plataformas.administrar` (28-oct-2026, decisión de Mariano — mismo
  // report): la misma clase, lo daba sólo `seed-permiso-plataformas-administrar.cjs`.
  // Abre el interruptor «Integración habilitada» de /api-terceros y el seed del
  // catálogo de plataformas. La palanca de interruptores (featureFlagsAdmin.js)
  // NO: va con `plataformas.interruptores`, que nace apagado y la siembra no da
  // a ningún rol (29-oct-2026, decisión de Mariano — mismo report).
  ...M('plataformas', ['administrar'], 'todo'),
  ...MODULOS_CATALOGO
    .filter(m => ['instalaciones', 'eventos', 'eventos-organizar'].includes(m.codigo))
    .flatMap(m => sembrables(m).map(f => P(m.codigo, f.codigo, 'todo'))),
]

// ─── Permisos finales por rol ─────────────────────────────────────
// Operativo completo para todos los no-Wizard; plataforma sólo donde la tenían.
// Staff = CONSULTOR, SUPERVISOR, OPERACIONES (por PLATAFORMA_CONSULTOR) y
// COLABORADOR_SOPORTE (suplencia de consultor). Que un rol de staff termine en
// un usuario cliente no lo frena la siembra sino el piso de staff del server.
const PERMISOS_OPERADOR            = [...PERMISOS_OPERATIVOS_COMPLETOS]
const PERMISOS_OWNER_EMPRESA       = [...PERMISOS_OPERATIVOS_COMPLETOS, ...PLATAFORMA_OWNER_EMPRESA]
const PERMISOS_CONSULTOR           = sinRepetir([...PERMISOS_OPERATIVOS_COMPLETOS, ...PLATAFORMA_CONSULTOR])
const PERMISOS_SUPERVISOR          = sinRepetir([...PERMISOS_OPERATIVOS_COMPLETOS, ...PLATAFORMA_SUPERVISOR])
const PERMISOS_OPERACIONES         = sinRepetir([...PERMISOS_OPERATIVOS_COMPLETOS, ...PLATAFORMA_OPERACIONES])
const PERMISOS_ASESOR              = [...PERMISOS_OPERATIVOS_COMPLETOS]
const PERMISOS_CONTADOR            = [...PERMISOS_OPERATIVOS_COMPLETOS]
const PERMISOS_AUDITOR             = [...PERMISOS_OPERATIVOS_COMPLETOS]
const PERMISOS_AGENCIA_PUBLICIDAD  = [...PERMISOS_OPERATIVOS_COMPLETOS]
const PERMISOS_COLABORADOR_SOPORTE = sinRepetir([...PERMISOS_OPERATIVOS_COMPLETOS, ...PERMISOS_SOLO_STAFF])

// ─── WIZARD — root. permisos vacío. esWizard atajo en código. ─────
const PERMISOS_WIZARD = [] // intencional. esWizard() cortocircuita.

// ─────────────────────────────────────────────────────────────────
// 3. Catálogo final de roles a sembrar
// ─────────────────────────────────────────────────────────────────

const ROLES_V2 = [
  // ── Wizard (root) ────────────────────────────────────────────
  {
    codigo: 'WIZARD',
    nombre: 'Wizard (root)',
    descripcion: 'Acceso total sin restricciones. Bootstrap, emergencias, soporte profundo. 1-2 personas en todo el sistema. Auditado con prioridad alta.',
    color: '#000000',
    orden: 1,
    eje: 'operativo',
    esRoot: true,
    permisoDefault: 'admin',
    extiende: null,
    aplicaA: ['sistema'],
    permisos: PERMISOS_WIZARD,
  },

  // ── Eje Operativo (cascada conceptual) ──────────────────────
  {
    codigo: 'OPERACIONES',
    nombre: 'Operaciones',
    descripcion: 'Staff Milbrands operando todas las empresas. Ve TODO lo operativo. Cuando empresas pasan a self-service total, este rol queda como soporte.',
    color: '#9333EA',
    orden: 10,
    eje: 'operativo',
    permisoDefault: 'admin',
    extiende: 'SUPERVISOR',
    aplicaA: ['consultor'],
    permisos: PERMISOS_OPERACIONES,
  },
  {
    codigo: 'SUPERVISOR',
    nombre: 'Supervisor',
    descripcion: 'Supervisa N consultores. Ve transitivamente las empresas de sus consultores. Puede reasignar entre ellos.',
    color: '#3B82F6',
    orden: 20,
    eje: 'operativo',
    permisoDefault: 'admin',
    extiende: 'CONSULTOR',
    aplicaA: ['consultor'],
    permisos: PERMISOS_SUPERVISOR,
  },
  {
    codigo: 'CONSULTOR',
    nombre: 'Consultor',
    descripcion: 'Staff Milbrands con N empresas asignadas. Opera completo sobre ellas. Etapa 1: gatekeeper. Etapa 2-3: asesor opcional.',
    color: '#06B6D4',
    orden: 30,
    eje: 'operativo',
    permisoDefault: 'editor',
    extiende: 'OWNER_EMPRESA',
    aplicaA: ['consultor'],
    permisos: PERMISOS_CONSULTOR,
  },
  {
    codigo: 'OWNER_EMPRESA',
    nombre: 'Owner de Empresa',
    descripcion: 'Designado por la empresa. Admin interno acotado a SU empresa. Puede gestionar usuarios e invitar consultores externos (self-service).',
    color: '#A855F7',
    orden: 40,
    eje: 'operativo',
    permisoDefault: 'admin',
    extiende: 'OPERADOR',
    aplicaA: ['cliente'],
    permisos: PERMISOS_OWNER_EMPRESA,
  },
  {
    codigo: 'OPERADOR',
    nombre: 'Operador',
    descripcion: 'Empleado de empresa que ejecuta tareas asignadas. Permisos básicos sobre recursos donde está involucrado.',
    color: '#F59E0B',
    orden: 50,
    eje: 'operativo',
    permisoDefault: 'viewer',
    extiende: null,
    aplicaA: ['cliente'],
    permisos: PERMISOS_OPERADOR,
  },

  // ── Roles laterales ─────────────────────────────────────────
  {
    codigo: 'ASESOR',
    nombre: 'Asesor',
    descripcion: 'Mirada externa que sugiere y comenta. Solo lectura + comentarios sobre recursos donde está involucrado. NO cierra, escala ni transfiere.',
    color: '#10B981',
    orden: 60,
    eje: 'operativo',
    permisoDefault: 'viewer',
    extiende: null,
    aplicaA: ['cliente', 'colaborador'],
    permisos: PERMISOS_ASESOR,
  },
  {
    codigo: 'CONTADOR',
    nombre: 'Contador',
    descripcion: 'Acceso acotado al área contable/fiscal. Filtros declarativos: tipoCaso (impuestos/facturacion) + area (contable/fiscal).',
    color: '#EAB308',
    orden: 70,
    eje: 'operativo',
    permisoDefault: 'editor',
    extiende: null,
    aplicaA: ['colaborador'],
    permisos: PERMISOS_CONTADOR,
  },
  {
    codigo: 'AUDITOR',
    nombre: 'Auditor',
    descripcion: 'Acceso de solo lectura para auditorías externas. Ventana temporal definida en la asignación (desde/hasta).',
    color: '#EF4444',
    orden: 80,
    eje: 'operativo',
    permisoDefault: 'viewer',
    extiende: null,
    aplicaA: ['colaborador'],
    permisos: PERMISOS_AUDITOR,
  },
  {
    codigo: 'AGENCIA_PUBLICIDAD',
    nombre: 'Agencia de Publicidad',
    descripcion: 'Agencia externa contratada por la empresa. Acotado a marketing/ventas con filtros declarativos.',
    color: '#EC4899',
    orden: 90,
    eje: 'operativo',
    permisoDefault: 'editor',
    extiende: null,
    aplicaA: ['colaborador'],
    permisos: PERMISOS_AGENCIA_PUBLICIDAD,
  },
  {
    codigo: 'COLABORADOR_SOPORTE',
    nombre: 'Colaborador de soporte',
    descripcion: 'Suplencia temporal de un consultor (vacaciones, ausencias). Aparece como punto de contacto para el cliente pero NO hereda casos/tareas del titular.',
    color: '#6B7280',
    orden: 100,
    eje: 'operativo',
    permisoDefault: 'viewer',
    extiende: null,
    aplicaA: ['consultor'],
    permisos: PERMISOS_COLABORADOR_SOPORTE,
  },
]

// ─────────────────────────────────────────────────────────────────
// 4. Función de seed (idempotente)
// ─────────────────────────────────────────────────────────────────

/**
 * Siembra los 11 roles v2 en la colección /roles.
 * Idempotente: pisa los campos canónicos (nombre, descripcion, eje, esRoot,
 * extiende, aplicaA, permisos) y respeta lo que el admin pudo haber tocado
 * en la UI (orden, color, esActivo).
 *
 * @param {{dryRun?: boolean}} opts
 * @returns {Promise<{seeded: string[], skipped: string[], poda: Object<string, string[]>, ejesDistintos: Array, rolesAjenos: Array}>}
 */
export async function seedRolesV2(opts = {}) {
  const { dryRun = false } = opts
  console.groupCollapsed(`[seedRolesV2] dryRun=${dryRun}`)

  const seeded = []
  const skipped = []
  const poda = {}
  const ejesDistintos = ejesDistintosDelCodigo()
  if (ejesDistintos.length > 0) {
    console.warn('[seedRolesV2] el eje en /modulos manda y difiere del código — estos módulos NO se podan:', ejesDistintos)
  }

  for (const r of ROLES_V2) {
    const ref = doc(db, COLLECTIONS.ROLES, r.codigo)
    const snap = await getDoc(ref)
    const existe = snap.exists()

    const payload = {
      codigo:           r.codigo,
      nombre:           r.nombre,
      descripcion:      r.descripcion,
      eje:              r.eje,
      esRoot:           !!r.esRoot,
      extiende:         r.extiende || null,
      aplicaA:          r.aplicaA || [],
      permisoDefault:   r.permisoDefault || 'viewer',
      permisos:         r.permisos,
      esSistema:        true,
      schemaVersion:    2,
      actualizadoEn:    serverTimestamp(),
    }

    if (!existe) {
      // Doc nuevo: setear todo incluyendo defaults.
      payload.color = r.color
      payload.orden = r.orden
      payload.esActivo = true
      payload.creadoEn = serverTimestamp()
      if (!dryRun) {
        await setDoc(ref, payload)
      }
      console.log(`[seedRolesV2] CREADO ${r.codigo} (${r.permisos.length} permisos)`)
      seeded.push(r.codigo)
    } else {
      // Doc existente: NO pisar color/orden/esActivo (los maneja el admin).
      payload.permisos = permisosAlResembrar(r.permisos, snap.data().permisos)
      poda[r.codigo] = paresQueSePodan(r.permisos, snap.data().permisos)
      if (poda[r.codigo].length > 0) console.log(`[seedRolesV2] PODA ${r.codigo}:`, poda[r.codigo])
      if (!dryRun) {
        await setDoc(ref, payload, { merge: true })
      }
      console.log(`[seedRolesV2] ACTUALIZADO ${r.codigo} (${payload.permisos.length} permisos) [merge]`)
      seeded.push(r.codigo)
    }
  }

  const todos = await getDocs(collection(db, COLLECTIONS.ROLES))
  const rolesAjenos = rolesAjenosConSoloStaff(todos.docs.map(d => ({ ...d.data(), codigo: d.id })), ROLES_V2.map(r => r.codigo))
  if (rolesAjenos.length > 0) {
    console.warn('[seedRolesV2] roles creados a mano, no de staff, con pares sólo-staff — se podan a mano en /permisos:', rolesAjenos)
  }
  console.log(`[seedRolesV2] total seeded=${seeded.length}`)
  console.groupEnd()
  return { seeded, skipped, poda, ejesDistintos, rolesAjenos }
}

/**
 * Lista los roles v2 que se sembrarían (para revisión sin tocar nada).
 * @returns {Array}
 */
export function listarRolesV2() {
  return ROLES_V2.map(r => ({
    codigo: r.codigo,
    nombre: r.nombre,
    eje: r.eje,
    extiende: r.extiende || null,
    esRoot: !!r.esRoot,
    numPermisos: r.permisos.length,
    permisos: r.permisos,
  }))
}

/**
 * Helper para auditar qué módulos cubre cada rol (debug).
 */
export function resumenPermisosPorRol() {
  return ROLES_V2.map(r => {
    const modulos = new Set()
    r.permisos.forEach(p => modulos.add(p.modulo))
    return {
      codigo: r.codigo,
      modulos: Array.from(modulos).sort(),
      totalPermisos: r.permisos.length,
    }
  })
}

// ─────────────────────────────────────────────────────────────────
// 5. Exposición en window para consola admin
// ─────────────────────────────────────────────────────────────────

if (typeof window !== 'undefined') {
  // eslint-disable-next-line no-undef
  window.__seedRolesV2 = seedRolesV2
  // eslint-disable-next-line no-undef
  window.__listarRolesV2 = listarRolesV2
  // eslint-disable-next-line no-undef
  window.__resumenPermisosPorRol = resumenPermisosPorRol
}
